import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import AppBar from "../../../components/atoms/appbar";
import Checkbox from "../../../components/atoms/checkbox";
import { useJoinTermsList, useMyInfo } from "../../../@apis/hooks/useAuth";
import { useModalStore } from "../../../lib/store/useModalStore";
import { updateMarketingAgree } from "../../../@apis/v1/auth.service";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response";
import { useMarketingTermsAgreeStore } from "../../../lib/store/useMarketingTermsAgreeStore";
import { TERMS_LABEL } from "../../../lib/constants/common.constants";

export default function OptionalTermsSettingsPage() {
  const { openModal } = useModalStore();
  const { data: termsData, isSuccess } = useQuery({ ...useJoinTermsList() });
  const { success, data } = termsData || {};
  const { data: myInfoData } = useQuery({ ...useMyInfo() });
  const myInfo = myInfoData?.data;

  // 선택(비필수) 약관만 노출
  const optionalTerms = (data?.termsList ?? []).filter(
    (term) => !term.isRequired,
  );

  // 체크 상태
  const [agreedIds, setAgreedIds] = useState<Record<number, boolean>>({});

  // 초기 체크값은 내 정보(my-info)의 마케팅 동의 여부로 설정
  const initializedRef = React.useRef(false);
  React.useEffect(() => {
    if (initializedRef.current) return;
    if (!isSuccess || !myInfo) return;
    const initial: Record<number, boolean> = {};
    optionalTerms.forEach((term) => {
      initial[term.id] =
        term.type === "marketing" ? myInfo.isMarketingAgreed : false;
    });
    setAgreedIds(initial);
    initializedRef.current = true;
  }, [isSuccess, myInfo, optionalTerms]);

  const updateMarketingAgreeMutation = useMutation({
    mutationFn: updateMarketingAgree,
    onError: (err: OnErrorResponse, variables) => {
      // 실패 시 토글 롤백
      setAgreedIds((prev) => ({
        ...prev,
        [variables.termsId]: !variables.agreed,
      }));
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "에러",
        content: error?.message ?? "마케팅 동의 처리에 실패했습니다.",
      });
    },
  });

  const toggleAgree = (id: number) => {
    if (updateMarketingAgreeMutation.isPending) return;
    const next = !agreedIds[id];
    // 낙관적 업데이트
    setAgreedIds((prev) => ({ ...prev, [id]: next }));
    updateMarketingAgreeMutation.mutate({ termsId: id, agreed: next });
  };

  // 약관 모달 하단 "동의" 버튼 신호 반영 (모달은 전역 렌더라 페이지 밖)
  const pendingAgreeId = useMarketingTermsAgreeStore((s) => s.pendingAgreeId);
  const clearPendingAgree = useMarketingTermsAgreeStore((s) => s.clear);
  React.useEffect(() => {
    if (pendingAgreeId == null) return;
    setAgreedIds((prev) => ({ ...prev, [pendingAgreeId]: true }));
    updateMarketingAgreeMutation.mutate({
      termsId: pendingAgreeId,
      agreed: true,
    });
    clearPendingAgree();
  }, [pendingAgreeId]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <AppBar backButton title="개인정보 동의 설정" />
      <div className="p-[20px]">
        <h1 className="text-xl font-medium mb-[20px]">선택 동의 관리</h1>

        <div className="mb-[30px]">
          {isSuccess &&
            success &&
            optionalTerms.map(({ id, type, term }) => {
              const checked = !!agreedIds[id];
              const openTermsModal = (e: React.MouseEvent) => {
                e.stopPropagation();
                openModal("TERMS_MODAL", {
                  html: term,
                  type,
                  // 이미 동의한 약관은 모달 하단 동의 버튼 미노출
                  agreeTermsId: checked ? undefined : id,
                  agreeChannel: "marketing",
                });
              };
              return (
                <div
                  key={id}
                  className="flex-row-between cursor-pointer select-none items-center gap-x-2"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleAgree(id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleAgree(id);
                    }
                  }}
                >
                  <Checkbox
                    className="size-4"
                    label={
                      <p>
                        <span className="text-[#8e8e93]">[선택] </span>
                        {TERMS_LABEL[type]} 동의
                      </p>
                    }
                    checked={checked}
                  />

                  <div
                    className="flex items-center font-semibold text-[#8e8e93] text-xs gap-x-[6px] shrink-0"
                    onClick={openTermsModal}
                  >
                    보기
                    <img
                      className="w-[6px] h-[14px] cursor-pointer"
                      src="/icons/ic-go.webp"
                      role="button"
                    />
                  </div>
                </div>
              );
            })}
        </div>
        <div className="bg-[#f2f2f7] py-[20px] px-[10px] text-[#8e8e93] rounded-[10px] flex text-sm">
          <span className="px-[6px] text-lg leading-none">·</span>
          선택 동의 항목은 언제든지 동의 내역을 변경하실 수 있습니다. 동의 철회
          시 일부 서비스 이용에 제한이 있을 수 있으나, 그 외 보이스파크 이용에
          불이익은 없습니다.
        </div>
      </div>
    </div>
  );
}
